/**
 * Analytics Module (GA4 + PostHog)
 *
 * Centralized, typed wrapper around Google Analytics 4 (gtag.js) and PostHog
 * for Suikamon. Every call site fires through the same typed helpers below,
 * so both providers stay in lockstep with zero extra call sites to maintain.
 *
 * Goals
 *  - Measure the funnel from first visit → new wallet owning its first Suikamon.
 *  - Fire business-outcome events only (no vanity metrics, no gameplay ticks).
 *  - Dedup one-shot milestones per wallet (first_suikamon_owned, first_pet_action,
 *    first_pvp_after_ownership) and per transaction (purchase).
 *  - Remain a safe no-op if gtag/PostHog are not loaded (ad blockers, SSR, tests).
 *
 * gtag.js is loaded via index.html. PostHog is loaded via the posthog-js npm
 * package and initialized from `useAnalytics` with VITE_POSTHOG_KEY.
 *
 * CONSENT FIRST (./analyticsConsent.ts): neither provider stores an identifier or receives an
 * event until the player accepts analytics. GA4 is gated here in `trackEvent`; PostHog starts
 * opted out in memory-only persistence. `applyAnalyticsConsent` flips both at once.
 */

import posthog from "posthog-js";
import type { CaptureResult } from "posthog-js";
import { classifyClientIssue, type ClientIssueDisposition } from "./clientIssueClassifier";
import { hasAnalyticsConsent, type AnalyticsConsent } from "./analyticsConsent";
import { getCampaignParams } from "./campaignSource";

// ---------------------------------------------------------------------------
// Globals
// ---------------------------------------------------------------------------

type GtagCommand = "event" | "config" | "set" | "consent";

type GtagFn = (command: GtagCommand, target: string, params?: Record<string, unknown>) => void;

declare global {
  interface Window {
    gtag?: GtagFn;
    dataLayer?: unknown[];
  }
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Namespace prefix for localStorage dedup keys. */
const LOCAL_STORAGE_PREFIX = "suikamon_ga4";

/**
 * Default PostHog ingestion host when VITE_POSTHOG_HOST is unset. Suikamon's
 * PostHog project lives in the EU region, so default there (not the US host).
 */
const DEFAULT_POSTHOG_HOST = "https://eu.i.posthog.com";
/** PostHog persistence with consent (its default) and without it (nothing written to the device). */
const POSTHOG_PERSISTENCE_CONSENTED = "localStorage+cookie";
const POSTHOG_PERSISTENCE_NO_CONSENT = "memory";

/**
 * GA4 requires the `currency` parameter on monetization events to use ISO 4217
 * (e.g. USD, EUR). SUI is not ISO 4217, so we report USD as a neutral placeholder
 * currency while keeping the authoritative SUI value on the `price_sui` parameter.
 */
const PLACEHOLDER_CURRENCY_ISO = "USD";

/** Used to tag all monetization events so dashboards can filter to SUI-priced items. */
const BLOCKCHAIN_CURRENCY = "SUI";

/** Length of the address prefix used as a non-PII wallet identifier. */
const WALLET_HASH_LENGTH = 10;

/** Events that must fire only once per wallet, ever. */
export const ONCE_PER_WALLET_EVENTS = [
  "first_suikamon_owned",
  "first_pet_action",
  "first_pvp_after_ownership",
] as const;

type OncePerWalletEvent = (typeof ONCE_PER_WALLET_EVENTS)[number];

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

let initialized = false;
let posthogInitialized = false;

/**
 * Marker PostHog's own SDK stamps on the console.error it logs when an ingestion
 * request fails to reach the analytics host (typically an ad blocker or a flaky
 * network — not an app bug). Because we enable `capture_console_errors`, PostHog
 * would otherwise capture its OWN transport failure as a `$exception`, flooding
 * Error Tracking with self-referential "Failed to fetch" noise carrying no signal.
 */
const ANALYTICS_TRANSPORT_ERROR_MARKER = "AnalyticsSDKApiError";

/**
 * Prefix the PostHog SDK puts on every console.error it emits about its OWN internals
 * ("Analytics SDK:" …). Same self-referential class as
 * {@link ANALYTICS_TRANSPORT_ERROR_MARKER}, different subsystem.
 */
const ANALYTICS_SDK_LOG_PREFIX = "Analytics SDK:";

/**
 * PostHog's persistence layer prefixes its storage faults `IndexedDB:<op>:<reason>`
 * (e.g. `IndexedDB:Set:InternalError`, seen on iOS Safari under storage pressure or
 * private browsing). It's the SDK's cache failing, never app state — Suikamon uses no
 * IndexedDB of its own. Matched as a VALUE PREFIX, not a substring of the whole property
 * bag, so a genuine app error that merely mentions IndexedDB still gets through.
 */
const ANALYTICS_STORAGE_ERROR_PREFIX = /^IndexedDB:\w+:/;

/**
 * `before_send` predicate: drop the self-referential exception PostHog captures when
 * its own ingestion request fails (marked {@link ANALYTICS_TRANSPORT_ERROR_MARKER}),
 * while letting every genuine app exception through — including real app-level
 * "Failed to fetch" errors, which lack the marker. Returns the event unchanged when
 * it should be kept, or `null` to drop it.
 *
 * Exported for unit testing; wired into `posthog.init` below.
 */
export function dropSelfReferentialAnalyticsErrors(
  result: CaptureResult | null,
): CaptureResult | null {
  if (result === null) return null;
  if (result.event !== "$exception") return result;

  // The SDK's own storage faults, matched on the exception VALUE only.
  const values = (result.properties as Record<string, unknown> | undefined)?.$exception_values;
  if (
    Array.isArray(values) &&
    values.some((v) => typeof v === "string" && ANALYTICS_STORAGE_ERROR_PREFIX.test(v))
  ) {
    return null;
  }

  try {
    // The marker can appear in the exception values or, since this originates as a
    // captured console.error, in the serialized console arguments — scan the whole
    // property bag rather than guessing PostHog's exact internal shape.
    const serialized = JSON.stringify(result.properties ?? {});
    if (
      serialized.includes(ANALYTICS_TRANSPORT_ERROR_MARKER) ||
      serialized.includes(ANALYTICS_SDK_LOG_PREFIX)
    ) {
      return null;
    }
  } catch {
    // A property that can't be serialized is not our transport-noise event — keep it.
    return result;
  }
  return result;
}

// ---------------------------------------------------------------------------
// PostHog exception → Telegram mirroring
// ---------------------------------------------------------------------------

/**
 * Prefix marking a Telegram error alert that originated from a PostHog-captured
 * `$exception` (unhandled error, unhandled rejection, or console.error). Lets the
 * ops channel tell these apart from the `[Uncaught]`/`[UnhandledRejection]` alerts
 * the global `errorReporting` listeners send.
 */
const POSTHOG_EXCEPTION_ALERT_PREFIX = "[PostHog]";

/** Max stack frames included in the Telegram context — enough to locate, not flood. */
const MAX_STACK_FRAMES = 3;

/** One entry of PostHog's `$exception_list` (the fields we read; the rest is ignored). */
interface PostHogExceptionEntry {
  type?: string;
  value?: string;
  stacktrace?: {
    frames?: Array<{
      filename?: string;
      function?: string;
      lineno?: number;
      colno?: number;
    }>;
  };
}

/** First entry of a PostHog `$exception_list`, or null when absent/empty. */
function firstExceptionEntry(properties: Record<string, unknown>): PostHogExceptionEntry | null {
  const list = properties.$exception_list;
  if (Array.isArray(list) && list.length > 0 && typeof list[0] === "object" && list[0] !== null) {
    return list[0] as PostHogExceptionEntry;
  }
  return null;
}

/** Build a readable "Type: message" line from a PostHog `$exception` event. */
export function posthogExceptionMessage(properties: Record<string, unknown>): string {
  const first = firstExceptionEntry(properties);
  if (first) {
    const type = typeof first.type === "string" ? first.type : "";
    const value = typeof first.value === "string" ? first.value : "";
    const combined = type && value ? `${type}: ${value}` : type || value;
    if (combined) return combined;
  }
  const message = properties.$exception_message;
  if (typeof message === "string" && message) return message;
  return "Unknown exception";
}

/** Build a compact context string (page + top stack frames) for the Telegram alert. */
function posthogExceptionContext(properties: Record<string, unknown>): string {
  const parts: string[] = [];
  try {
    if (typeof window !== "undefined") parts.push(`page=${window.location.href}`);
  } catch {
    /* window.location can throw in exotic sandboxes — skip it */
  }
  const frames = firstExceptionEntry(properties)?.stacktrace?.frames;
  if (Array.isArray(frames) && frames.length > 0) {
    const stack = frames
      .slice(0, MAX_STACK_FRAMES)
      .map(
        (f) =>
          `  at ${f.function ?? "?"} (${f.filename ?? "?"}:${f.lineno ?? "?"}:${f.colno ?? "?"})`,
      )
      .join("\n");
    if (stack) parts.push(stack);
  }
  return parts.join("\n");
}

/**
 * Forward a PostHog-captured `$exception` to the backend so it lands in Render's logs and
 * the Telegram private chat. Routes by the already-computed {@link ClientIssueDisposition}:
 * `warn` → `logClientWarning` (Render `logger.warn`, no Telegram page — expected-environment
 * failures like Safari's "Load failed"), anything else → `logClientError` (Render `logger.error`
 * + Telegram alert). `ignore` is never passed here — the caller drops that noise from every
 * channel, PostHog included.
 *
 * `logClientError` de-dupes per message (10s) and caps overall (10/min) — and swallows its own
 * fetch failures — so this can't spam or loop back through PostHog. Never throws: it runs inside
 * `before_send`, which must return the event synchronously.
 */
export function forwardPostHogExceptionToTelegram(
  message: string,
  context: string,
  disposition: ClientIssueDisposition,
): void {
  try {
    // Lazy import: keep this widely-imported analytics module free of a load-time
    // dependency on `clientLogApi` → `apiConfig` (which throws when VITE_API_URL is
    // unset). `logClientError`/`logClientWarning` are fire-and-forget, so the floating
    // promise is fine.
    void import("../api/clientLogApi")
      .then(({ logClientError, logClientWarning }) => {
        const report = `${POSTHOG_EXCEPTION_ALERT_PREFIX} ${message}`;
        if (disposition === "warn") logClientWarning(report, context);
        else logClientError(report, context);
      })
      .catch(() => {
        /* never let error forwarding surface as its own failure */
      });
  } catch {
    /* error forwarding must never break analytics ingestion */
  }
}

/**
 * PostHog `before_send` handler. One classification decision drives all three tracking
 * channels — PostHog, Render logs, and Telegram — so they never diverge:
 *
 *  - First drop PostHog's self-referential ingestion-failure noise.
 *  - Then run every genuine `$exception` through the SAME {@link classifyClientIssue} filter the
 *    global window listeners use, and act on its verdict identically across channels:
 *      • `ignore` (wallet-extension self-reports like Rabby's `eth_accounts`, RainbowKit probes,
 *        TRPC, user-rejected prompts) → drop from PostHog (return `null`) AND skip the backend,
 *        so it appears in none of the three.
 *      • `warn` (Safari "Load failed", stale-chunk imports) → keep in PostHog, `logClientWarning`
 *        (Render warn; not paged to Telegram).
 *      • `error` (real app crashes) → keep in PostHog, `logClientError` (Render error + Telegram).
 *
 * Returns the event PostHog should ingest, or `null` to drop it.
 */
export function analyticsBeforeSend(result: CaptureResult | null): CaptureResult | null {
  const kept = dropSelfReferentialAnalyticsErrors(result);
  if (!kept || kept.event !== "$exception") return kept;

  const properties = (kept.properties ?? {}) as Record<string, unknown>;
  const message = posthogExceptionMessage(properties);
  const context = posthogExceptionContext(properties);
  const disposition = classifyClientIssue(message, context);

  // Noise is not a Suikamon error: drop it from PostHog too so Error Tracking, Render logs,
  // and Telegram all agree on what counts as an error.
  if (disposition === "ignore") return null;

  forwardPostHogExceptionToTelegram(message, context, disposition);
  return kept;
}

/**
 * Hook the GA4 configuration for SPA usage.
 *
 * We disable gtag's automatic `page_view` sending because we fire page views
 * ourselves on every React Router transition — this avoids duplicates between
 * gtag's enhanced measurement and our manual instrumentation.
 *
 * Safe to call multiple times (idempotent).
 */
export function initializeAnalytics(measurementId: string | undefined): void {
  if (initialized) return;
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") {
    // gtag.js hasn't loaded yet (ad blocker, offline, tests). Mark as initialized
    // so we don't keep re-checking, but every trackEvent will no-op safely.
    initialized = true;
    return;
  }

  if (measurementId) {
    window.gtag("config", measurementId, {
      send_page_view: false,
      // Surface events in GA4 DebugView during local development.
      debug_mode: import.meta.env.DEV,
    });
  }
  initialized = true;
}

/**
 * Initialize PostHog product analytics (session replay off by default —
 * enable it in the PostHog project settings, not here).
 *
 * We disable PostHog's own pageview autocapture because we fire `page_view`
 * ourselves on every React Router transition via `trackEvent`, same as GA4.
 *
 * Safe to call multiple times (idempotent) and a safe no-op without an API key
 * (local dev / PR previews without a configured PostHog project).
 */
export function initializePostHogAnalytics(
  apiKey: string | undefined,
  apiHost: string | undefined,
): void {
  if (posthogInitialized) return;
  if (typeof window === "undefined") return;
  if (!apiKey) {
    // No PostHog project configured for this environment — mark initialized
    // so every trackEvent call below no-ops safely via posthog.__loaded.
    posthogInitialized = true;
    return;
  }

  const consented = hasAnalyticsConsent();
  posthog.init(apiKey, {
    api_host: apiHost || DEFAULT_POSTHOG_HOST,
    // Consent first: without an explicit yes PostHog captures nothing, keeps nothing on the
    // device and records no session. applyAnalyticsConsent() lifts all three on "Accept".
    opt_out_capturing_by_default: !consented,
    persistence: consented ? POSTHOG_PERSISTENCE_CONSENTED : POSTHOG_PERSISTENCE_NO_CONSENT,
    disable_session_recording: !consented,
    capture_pageview: false,
    debug: import.meta.env.DEV,
    // Error Tracking: autocapture unhandled JS errors + promise rejections so
    // production exceptions land in PostHog (off by default — this was why the
    // Error Tracking view was empty).
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: true,
    },
    // Attach console.log/warn/error to session recordings (off by default — this
    // was why recordings carried no logs).
    enable_recording_console_log: true,
    // Drop PostHog's own ingestion-failure exceptions (ad blocker / network) so they
    // don't flood Error Tracking as self-referential "Failed to fetch" noise, and mirror
    // every genuine exception PostHog captures to the Telegram private chat so the two
    // stay in lockstep (this also covers console.error captures the global error
    // listeners never see).
    before_send: analyticsBeforeSend,
  });
  posthogInitialized = true;
}

/**
 * Apply the player's analytics choice to both providers at once. GA4: Consent Mode v2 update
 * (ad storage stays denied — the game shows no ads). PostHog: opt in with normal persistence and
 * allow session recording, or opt out and fall back to memory-only persistence.
 */
export function applyAnalyticsConsent(choice: AnalyticsConsent | null): void {
  if (typeof window === "undefined") return;
  const granted = choice === "granted";

  if (typeof window.gtag === "function") {
    try {
      window.gtag("consent", "update", { analytics_storage: granted ? "granted" : "denied" });
    } catch (err) {
      console.warn("[analytics] gtag consent update failed", err);
    }
  }

  if (posthog.__loaded) {
    try {
      if (granted) {
        posthog.set_config({ persistence: POSTHOG_PERSISTENCE_CONSENTED, disable_session_recording: false });
        posthog.opt_in_capturing();
      } else {
        posthog.opt_out_capturing();
        posthog.set_config({ persistence: POSTHOG_PERSISTENCE_NO_CONSENT, disable_session_recording: true });
      }
    } catch (err) {
      console.warn("[analytics] posthog consent update failed", err);
    }
  }
}

// ---------------------------------------------------------------------------
// Low-level primitives
// ---------------------------------------------------------------------------

function stripNullish(params: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    result[key] = value;
  }
  return result;
}

/**
 * Fire a GA4 event. Safe no-op if gtag is not loaded.
 *
 * All production consumers should go through one of the typed helpers below
 * rather than calling this directly, so event names stay in sync with the
 * documented taxonomy.
 */
export function trackEvent(eventName: string, params: object = {}): void {
  if (typeof window === "undefined") return;

  // Every event carries the channel this visit came from (a campaign short link), if any.
  const payload = stripNullish({ ...getCampaignParams(), ...(params as Record<string, unknown>) });

  // GA4 hears nothing without consent (PostHog is opted out at init, so it no-ops on its own).
  const gtag = hasAnalyticsConsent() ? window.gtag : undefined;
  if (typeof gtag === "function") {
    try {
      gtag("event", eventName, {
        ...payload,
        // Enable DebugView for dev builds without needing a browser extension.
        ...(import.meta.env.DEV ? { debug_mode: true } : {}),
      });
    } catch (err) {
      // Never let analytics break the app.
      console.warn("[analytics] gtag call failed", err);
    }

    if (import.meta.env.DEV) {
       
      console.debug("[ga4]", eventName, payload);
    }
  }

  if (posthog.__loaded) {
    try {
      posthog.capture(eventName, payload);
    } catch (err) {
      // Never let analytics break the app.
      console.warn("[analytics] posthog capture failed", err);
    }

    if (import.meta.env.DEV) {
       
      console.debug("[posthog]", eventName, payload);
    }
  }
}

// ---------------------------------------------------------------------------
// Dedup helpers
// ---------------------------------------------------------------------------

function safeLocalStorageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key: string, value: string): boolean {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function normalizeWallet(walletAddress: string): string {
  return walletAddress.trim().toLowerCase();
}

function dedupKeyForWallet(eventName: OncePerWalletEvent, walletAddress: string): string {
  return `${LOCAL_STORAGE_PREFIX}:${eventName}:${normalizeWallet(walletAddress)}`;
}

function dedupKeyForTransaction(transactionId: string): string {
  return `${LOCAL_STORAGE_PREFIX}:purchase:${transactionId}`;
}

function hashWallet(walletAddress: string): string {
  return normalizeWallet(walletAddress).slice(0, WALLET_HASH_LENGTH);
}

/**
 * Fire `eventName` only the first time it is seen for this wallet.
 *
 * Dedup persists in localStorage so the guarantee survives refreshes
 * and is stable across tabs for the same browser profile.
 *
 * @returns true if the event was actually fired, false if it was deduped away.
 */
export function trackOncePerWallet(
  eventName: OncePerWalletEvent,
  walletAddress: string,
  params: object = {},
): boolean {
  if (!walletAddress) return false;
  if (typeof window === "undefined") return false;

  const key = dedupKeyForWallet(eventName, walletAddress);
  if (safeLocalStorageGet(key)) return false;

  const written = safeLocalStorageSet(key, String(Date.now()));
  // If localStorage is unavailable, refuse to fire to respect "once per wallet".
  if (!written) return false;

  trackEvent(eventName, {
    wallet_address_hash: hashWallet(walletAddress),
    ...(params as Record<string, unknown>),
  });
  return true;
}

// ---------------------------------------------------------------------------
// Page view tracking
// ---------------------------------------------------------------------------

/**
 * Routes that get a dedicated view event (in addition to generic `page_view`).
 *
 * Keep this in lockstep with the GA4 tracking spec §5.2 (Page Context events).
 */
export const PAGE_VIEW_EVENT_MAP = Object.freeze({
  "/": "landing_page_view",
  "/adopt": "adopt_page_view",
  "/pvp-lab": "pvp_lab_view",
  "/pvp-trial": "pvp_trial_view",
  "/pvp-ranked": "pvp_ranked_view",
  "/help": "help_view",
  "/whitepaper": "whitepaper_view",
} as const);

export type PageType =
  | "home"
  | "adopt"
  | "play"
  | "rankings"
  | "trainers"
  | "help"
  | "pvp_lab"
  | "pvp_trial"
  | "pvp_ranked"
  | "whitepaper"
  | "terms"
  | "privacy"
  | "pet_detail"
  | "trainer_profile"
  | "species_detail"
  | "battle"
  | "media"
  | "news"
  | "other";

interface TrackPageViewParams {
  path: string;
  pageType?: PageType;
  /** Extra parameters to attach to the page_view event (e.g. species). */
  extra?: Record<string, unknown>;
}

/**
 * Fire a generic `page_view` event on every navigation and, when applicable,
 * a specific view event (e.g. `adopt_page_view`).
 *
 * Species detail pages live at `/{speciesName}` and are routed through
 * `SpeciesPage`, which fires `species_page_view` itself with the species
 * name — avoid duplicating that here.
 */
export function trackPageView({ path, pageType, extra }: TrackPageViewParams): void {
  trackEvent("page_view", {
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.href : undefined,
    page_title: typeof document !== "undefined" ? document.title : undefined,
    page_type: pageType,
    ...extra,
  });

  const specificEvent = PAGE_VIEW_EVENT_MAP[path as keyof typeof PAGE_VIEW_EVENT_MAP];
  if (specificEvent) {
    trackEvent(specificEvent, {
      page_path: path,
      page_type: pageType,
      ...extra,
    });
  }
}

/**
 * Fire `species_page_view` for a specific species. This is called from
 * `SpeciesPage` rather than from the generic route tracker because only
 * the page component knows whether the URL segment resolves to a real
 * species.
 */
export function trackSpeciesPageView(params: {
  species: string;
  rarity?: string;
  path: string;
}): void {
  trackEvent("species_page_view", {
    species: params.species,
    rarity: params.rarity,
    page_path: params.path,
    page_type: "species_detail" satisfies PageType,
  });
}

// ---------------------------------------------------------------------------
// Wallet funnel
// ---------------------------------------------------------------------------

interface WalletEventParams {
  wallet_type?: string;
  page_path?: string;
}

export function trackWalletConnectStart(params: WalletEventParams = {}): void {
  trackEvent("wallet_connect_start", params);
}

export function trackWalletConnectSuccess(
  walletAddress: string,
  params: WalletEventParams = {},
): void {
  const walletAddressHash = hashWallet(walletAddress);

  // Stitch this browser's PostHog anonymous history to a stable pseudonymous
  // ID so funnels/retention survive across sessions. Mirrors the non-PII
  // wallet_address_hash already sent to GA4 — never the raw wallet address.
  if (posthog.__loaded) {
    try {
      posthog.identify(walletAddressHash);
    } catch (err) {
      console.warn("[analytics] posthog identify failed", err);
    }
  }

  trackEvent("wallet_connect_success", {
    ...params,
    wallet_address_hash: walletAddressHash,
  });
}

export function trackWalletConnectError(
  errorMessage: string,
  params: WalletEventParams = {},
): void {
  trackEvent("wallet_connect_error", {
    ...params,
    error: errorMessage.slice(0, 200),
  });
}

// ---------------------------------------------------------------------------
// Adoption / purchase funnel
// ---------------------------------------------------------------------------

export type AcquisitionPath =
  | "direct_adopt"
  | "referral"
  | "pvp_lab"
  | "airdrop";

export interface AdoptionBaseParams {
  species?: string;
  species_id?: number;
  rarity?: string;
  price_sui?: number;
  wallet_connected?: boolean;
  acquisition_path?: AcquisitionPath;
  discount_type?: "species" | "referral" | "pvp" | "airdrop" | "none";
  discount_value?: number;
}

export function trackSpeciesSelect(params: AdoptionBaseParams): void {
  trackEvent("species_select", params);
}

export function trackBeginCheckout(params: AdoptionBaseParams): void {
  trackEvent("begin_checkout", {
    currency: PLACEHOLDER_CURRENCY_ISO,
    blockchain_currency: BLOCKCHAIN_CURRENCY,
    value: params.price_sui ?? 0,
    ...params,
  });
}

export function trackPurchaseAttempt(params: AdoptionBaseParams): void {
  trackEvent("purchase_attempt", {
    currency: PLACEHOLDER_CURRENCY_ISO,
    blockchain_currency: BLOCKCHAIN_CURRENCY,
    value: params.price_sui ?? 0,
    ...params,
  });
}

export interface PurchaseParams extends AdoptionBaseParams {
  /** Sui transaction digest — used for GA4 purchase dedup. */
  transaction_id: string;
  pet_id?: string;
  pet_count_after_purchase?: number;
  is_first_pet?: boolean;
}

/**
 * Fire GA4 recommended `purchase` event.
 *
 * Deduped per transaction_id so page reloads after success cannot double-count.
 */
export function trackPurchase(params: PurchaseParams): void {
  if (!params.transaction_id) {
    // Can't dedup without a txn id — skip to avoid inflation.
    return;
  }

  const key = dedupKeyForTransaction(params.transaction_id);
  if (safeLocalStorageGet(key)) return;
  safeLocalStorageSet(key, String(Date.now()));

  trackEvent("purchase", {
    currency: PLACEHOLDER_CURRENCY_ISO,
    blockchain_currency: BLOCKCHAIN_CURRENCY,
    value: params.price_sui ?? 0,
    ...params,
  });
}

export function trackPurchaseFail(
  errorMessage: string,
  params: AdoptionBaseParams = {},
): void {
  trackEvent("purchase_fail", {
    ...params,
    currency: PLACEHOLDER_CURRENCY_ISO,
    blockchain_currency: BLOCKCHAIN_CURRENCY,
    value: params.price_sui ?? 0,
    error: errorMessage.slice(0, 200),
  });
}

// ---------------------------------------------------------------------------
// Core conversion + activation
// ---------------------------------------------------------------------------

export interface FirstOwnershipParams {
  species?: string;
  rarity?: string;
  pet_id?: string;
  pet_count_after_purchase?: number;
  acquisition_path?: AcquisitionPath;
}

/**
 * Core conversion: fires the first time a wallet is observed owning
 * at least one Suikamon. Deduped per wallet via localStorage.
 */
export function trackFirstSuikamonOwned(
  walletAddress: string,
  params: FirstOwnershipParams = {},
): boolean {
  return trackOncePerWallet("first_suikamon_owned", walletAddress, params);
}

/**
 * Activation: fires the first pet action a wallet performs after owning a pet.
 * Deduped per wallet.
 */
export function trackFirstPetAction(
  walletAddress: string,
  action: string,
): boolean {
  return trackOncePerWallet("first_pet_action", walletAddress, { action });
}

/**
 * Activation: fires the first PVP battle a wallet starts after owning a pet.
 * Deduped per wallet.
 */
export function trackFirstPvpAfterOwnership(
  walletAddress: string,
  params: { mode?: string; species?: string; rarity?: string } = {},
): boolean {
  return trackOncePerWallet("first_pvp_after_ownership", walletAddress, params);
}

// ---------------------------------------------------------------------------
// Engagement
// ---------------------------------------------------------------------------

export interface CtaClickParams {
  cta: string;
  page_path?: string;
  destination?: string;
}

export function trackCtaClick(params: CtaClickParams): void {
  trackEvent("cta_click", params);
}

export interface DiscountEventParams {
  discount_type: "species" | "referral" | "pvp" | "airdrop";
  discount_value?: number;
  species?: string;
  rarity?: string;
}

export function trackDiscountView(params: DiscountEventParams): void {
  trackEvent("discount_view", params);
}

export function trackDiscountClaim(params: DiscountEventParams): void {
  trackEvent("discount_claim", params);
}

// ---------------------------------------------------------------------------
// New-wallet activation flow
// ---------------------------------------------------------------------------

/**
 * Measures the login → lab/adopt funnel for the one-time new-wallet activation modal.
 * Per-account dedup lives in the modal's localStorage guard, so these are plain events.
 */
export function trackNewWalletActivationShown(): void {
  trackEvent("new_wallet_activation_shown");
}

export function trackNewWalletActivationPlayLabClick(): void {
  trackEvent("new_wallet_activation_play_lab_click");
}

export function trackNewWalletActivationAdoptClick(): void {
  trackEvent("new_wallet_activation_adopt_click");
}
